import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import { Button } from './button';
import { ImageIcon, Bold, Italic, Underline as UnderlineIcon, Link as LinkIcon, Maximize2, Minimize2, AlignLeft, AlignCenter, AlignRight } from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
  placeholder?: string;
}

export function RichTextEditor({ value, onChange, readOnly = false, placeholder }: RichTextEditorProps) {
  const [imageUrl, setImageUrl] = useState('');
  const [selectedImage, setSelectedImage] = useState<{ node: any; pos: number } | null>(null);
  const [imagePopupPosition, setImagePopupPosition] = useState({ x: 0, y: 0 });

  // Re-create the editor when readOnly changes to ensure editable is respected
  const [editorKey, setEditorKey] = useState(0);
  useEffect(() => {
    setEditorKey(prev => prev + 1);
  }, [readOnly]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Image.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            width: {
              default: '400px',
              parseHTML: element => element.getAttribute('width') || '400px',
              renderHTML: attributes => ({
                width: attributes.width,
                style: `width: ${attributes.width}; height: auto;`
              })
            },
            align: {
              default: 'left',
              parseHTML: element => element.getAttribute('data-align') || 'left',
              renderHTML: attributes => ({
                'data-align': attributes.align,
                style: `width: ${attributes.width}; height: auto; display: block; margin: ${attributes.align === 'center' ? '0 auto' : attributes.align === 'right' ? '0 0 0 auto' : '0'};`
              })
            }
          }
        }
      }).configure({
        HTMLAttributes: {
          class: 'max-w-full h-auto rounded-lg cursor-pointer',
        },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-500 underline',
        },
      }),
    ],
    content: value,
    editable: !readOnly,
    onUpdate: ({ editor }) => {
      console.log('Editor update:', editor.getHTML());
      onChange(editor.getHTML());
    },
  }, [editorKey, readOnly]);

  const handleImageUrlSubmit = () => {
    if (imageUrl && editor) {
      console.log('Inserting image:', imageUrl);
      editor.chain()
        .focus()
        .setImage({ 
          src: imageUrl,
          alt: '',
          title: ''
        })
        .run();
      setImageUrl('');
    }
  };

  const handleImageClick = useCallback((event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    if (target.tagName === 'IMG' && editor) {
      console.log('Image clicked:', target);
      const rect = target.getBoundingClientRect();
      setImagePopupPosition({
        x: rect.left,
        y: rect.bottom + window.scrollY + 5,
      });

      const pos = editor.view.posAtDOM(target, 0);
      const node = editor.view.state.doc.nodeAt(pos);
      console.log('Image node:', node, 'at pos:', pos);
      
      if (node) {
        setSelectedImage({ node, pos });
      }
    } else {
      setSelectedImage(null);
    }
  }, [editor]);

  const updateImageSize = useCallback((size: 'small' | 'medium' | 'large') => {
    if (selectedImage && editor) {
      console.log('Updating image size:', size, selectedImage);
      const sizes = {
        small: '200px',
        medium: '400px',
        large: '600px',
      };

      editor.chain()
        .focus()
        .setNodeSelection(selectedImage.pos)
        .updateAttributes('image', {
          width: sizes[size]
        })
        .run();

      setSelectedImage(null);
    }
  }, [selectedImage, editor]);

  const updateImageAlign = useCallback((align: 'left' | 'center' | 'right') => {
    if (selectedImage && editor) {
      console.log('Updating image alignment:', align, selectedImage);

      editor.chain()
        .focus()
        .setNodeSelection(selectedImage.pos)
        .updateAttributes('image', {
          align
        })
        .run();

      setSelectedImage(null);
    }
  }, [selectedImage, editor]);

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectedImage && !(event.target as HTMLElement).closest('.image-popup')) {
        setSelectedImage(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectedImage]);

  const setLink = () => {
    const previousUrl = editor?.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);

    if (url === null) {
      return;
    }

    if (url === '') {
      editor?.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor?.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <div className="relative">
      <style>{`
        .image-base {
          max-width: 100%;
          height: auto;
          border-radius: 0.5rem;
          cursor: pointer;
        }
        .image-small {
          width: 200px !important;
        }
        .image-medium {
          width: 400px !important;
        }
        .image-large {
          width: 600px !important;
        }
      `}</style>
      <div className={`border rounded-md ${readOnly ? 'bg-gray-100' : ''}`}> 
        {!readOnly && (
          <>
            <div className="p-1.5 flex items-center gap-0.5 border-b flex-wrap">
              <div className="flex items-center gap-0.5">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => editor?.chain().focus().toggleBold().run()}
                  className={`p-1 h-7 w-7 ${editor?.isActive('bold') ? 'bg-gray-100' : ''}`}
                >
                  <Bold className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => editor?.chain().focus().toggleItalic().run()}
                  className={`p-1 h-7 w-7 ${editor?.isActive('italic') ? 'bg-gray-100' : ''}`}
                >
                  <Italic className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => editor?.chain().focus().toggleUnderline().run()}
                  className={`p-1 h-7 w-7 ${editor?.isActive('underline') ? 'bg-gray-100' : ''}`}
                >
                  <UnderlineIcon className="h-3.5 w-3.5" />
                </Button>
              </div>
              <div className="h-4 w-px bg-gray-300 mx-0.5" />
              <Button
                variant="ghost"
                size="sm"
                onClick={setLink}
                className={`p-1 h-7 w-7 ${editor?.isActive('link') ? 'bg-gray-100' : ''}`}
              >
                <LinkIcon className="h-3.5 w-3.5" />
              </Button>
            </div>
          </>
        )}
        {!readOnly && (
          <div className="p-1.5 flex items-center gap-2">
            <input
              type="text"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="Enter image URL"
              className="flex-1 px-2 py-1 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={handleImageUrlSubmit}
              disabled={!imageUrl}
              className="hover:bg-gray-100 h-7 px-2"
            >
              <ImageIcon className="h-3.5 w-3.5 mr-1" />
              Insert Image
            </Button>
          </div>
        )}
        {selectedImage && (
          <div 
            className="fixed z-50 bg-white border rounded-md shadow-lg p-2 flex flex-col gap-2 image-popup"
            style={{
              left: `${imagePopupPosition.x}px`,
              top: `${imagePopupPosition.y}px`,
            }}
          >
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => updateImageSize('small')}
                className="hover:bg-gray-100 h-7 px-2"
              >
                <Minimize2 className="h-3.5 w-3.5 mr-1" />
                Small
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => updateImageSize('medium')}
                className="hover:bg-gray-100 h-7 px-2"
              >
                <ImageIcon className="h-3.5 w-3.5 mr-1" />
                Medium
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => updateImageSize('large')}
                className="hover:bg-gray-100 h-7 px-2"
              >
                <Maximize2 className="h-3.5 w-3.5 mr-1" />
                Large
              </Button>
            </div>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => updateImageAlign('left')}
                className="hover:bg-gray-100 h-7 px-2"
              >
                <AlignLeft className="h-3.5 w-3.5 mr-1" />
                Left
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => updateImageAlign('center')}
                className="hover:bg-gray-100 h-7 px-2"
              >
                <AlignCenter className="h-3.5 w-3.5 mr-1" />
                Center
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => updateImageAlign('right')}
                className="hover:bg-gray-100 h-7 px-2"
              >
                <AlignRight className="h-3.5 w-3.5 mr-1" />
                Right
              </Button>
            </div>
          </div>
        )}
        <EditorContent
          key={editorKey}
          editor={editor} 
          className={`prose prose-sm max-w-none p-4 min-h-[200px] border border-gray-300 rounded-b-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${readOnly ? 'bg-gray-100 cursor-not-allowed text-gray-500' : 'bg-white'}`}
          onClick={handleImageClick}
        />
      </div>
    </div>
  );
} 